import { isObject } from '@kk/shared/utils/type-guards';

export function isErrorResponse(response: unknown): response is Response {
  return isObject(response) && 'status' in response && 'headers' in response;
}

function getOperationId(error: unknown): string | undefined {
  if (error instanceof Error && error.cause) {
    const operationIds = (error.cause as { operationIds?: string[] })
      .operationIds;
    if (Array.isArray(operationIds) && operationIds.length > 0) {
      return operationIds.join(', ');
    }
  }
  if (isErrorResponse(error)) {
    return error.headers.get('Operationid') ?? undefined;
  }
}

export function assertNoLoaderErrors<T extends [...(Error | T[number])[]]>(
  id: string,
  data: T,
  ...args
): asserts data is { [K in keyof T]: Exclude<T[K], Error> } {
  if (data.some((value: unknown) => value instanceof Error)) {
    throw new LoaderError(id, data, ...args);
  }
}

class LoaderError extends Error {
  errorResponses: Array<Error | unknown>;
  constructor(id: string, responses: Array<Error | unknown>, ...args) {
    const formattedErrors = responses
      .map((response, index) => {
        if (response instanceof Error) {
          return `#${index + 1} - ${response.message}`;
        }
        return null;
      })
      .filter(Boolean);

    const operationIds = responses
      .map((response) => getOperationId(response))
      .filter(Boolean);

    const errorMessage = `Failed to load ${id} data: \n\n${formattedErrors.join(
      '\n',
    )}`;

    super(errorMessage, ...args, { cause: { operationIds }, responses });
    this.errorResponses = responses.filter(isErrorResponse);
  }
}

export function isLoaderError(error: unknown): error is LoaderError {
  return error instanceof LoaderError;
}
